import classnames from 'classnames';
import Button from 'components/actions/Button';
import IconButton from 'components/actions/IconButton';
import DateTimeOrNoData from 'components/content/DateTimeOrNoData';
import NoData from 'components/content/NoData';
import Sidebar from 'components/navigation/Sidebar';
import Tooltip from 'components/overlays/Tooltip';
import type { UserFE } from 'domain/systemAdmin';
import { SystemAdminDialogTypes } from 'helper/types';
import { useDispatch, useSelector } from 'react-redux';
import {
    ActiveSidebar,
    clearActiveElements,
    getActiveSidebar,
    getActiveUser,
    setActiveUser,
    setSystemAdminOpenDialog,
    setUserDialog,
    toogleShowRoleDialog,
} from 'store/slices/settingsPageSlice';

import Can from '../../../components/auth/Can';
import SidebarSection, { type SectionItem } from '../SidebarSection';
import SystemUsersRowStatus from './SystemUsersRowStatus';

import './styles/SystemUsersSidebar.scss';

interface FooterProps {
    openUserDialog: () => void;
    openResetPasswordDialog: () => void;
    openDeleteUserDialog: () => void;
    user?: UserFE;
}

const Footer = ({ openUserDialog, openResetPasswordDialog, openDeleteUserDialog, user }: FooterProps) => (
    <div className='SystemUsersSidebar__footer'>
        <Can type='users' action='edit'>
            <Button variant='outline' style='primary' className='SystemUsersSidebar__button' onClick={openUserDialog}>
                <span className='rioglyph rioglyph-pencil mr-5' />
                Edit
            </Button>
        </Can>
        <Button
            variant='outline'
            style='primary'
            className={classnames('SystemUsersSidebar__button', {
                'SystemUsersSidebar__button--hidden': user?.status !== 'ACTIVE',
            })}
            onClick={openResetPasswordDialog}
        >
            <span className='rioglyph rioglyph-key mr-5' />
            Reset password
        </Button>
        <Can type='users' action='delete'>
            <Tooltip
                placement='top-end'
                className='SystemUsersSidebar__deleteTooltip'
                innerClassName='SystemUsersSidebar__deleteTooltipInner'
                classNames='SystemUsersSidebar__deleteTooltipTrigger'
                trigger={
                    <IconButton
                        variant='outline'
                        style='danger'
                        className='SystemUsersSidebar__button--cancel'
                        onClick={openDeleteUserDialog}
                        disabled={!user}
                        data-testid='system-users-sidebar-delete-user'
                    >
                        <span className='rioglyph rioglyph-trash' />
                    </IconButton>
                }
            >
                <span>Delete</span>
            </Tooltip>
        </Can>
    </div>
);

const SystemUsersSidebar = () => {
    const dispatch = useDispatch();
    const activeSidebar = useSelector(getActiveSidebar);
    const activeUser = useSelector(getActiveUser);
    const toogleRoleDialog = () => {
        dispatch(toogleShowRoleDialog());
    };

    const openUserDialog = (user: UserFE | undefined) => {
        dispatch(setUserDialog({ user, showUserDialog: true }));
    };

    const openResetPasswordDialog = () => {
        dispatch(setActiveUser(activeUser));
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.RESET_PASSWORD_DIALOG));
    };

    const openDeleteUserDialog = () => {
        dispatch(setActiveUser(activeUser));
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.DELETE_USER_DIALOG));
    };

    const personalItems: SectionItem[] = [
        {
            label: 'E-mail',
            element: activeUser?.email,
        },
        {
            label: 'Phone',
            element: activeUser?.phone ? activeUser?.phone : <NoData className='text-14' />,
        },
    ];

    const workLocationItems: SectionItem[] = [
        {
            label: 'Country',
            element: activeUser?.country ? activeUser?.country : <NoData className='text-14' />,
        },
        {
            label: 'City',
            element: activeUser?.city ? activeUser?.city : <NoData className='text-14' />,
        },
    ];

    const operationalItems: SectionItem[] = [
        {
            label: 'Team',
            element: activeUser?.teams.length ? (
                activeUser.teams.map((team, index) => <p key={index}>{team.name}</p>)
            ) : (
                <NoData className='text-14' />
            ),
        },
        {
            label: (
                <>
                    Role
                    <span
                        className='rioglyph rioglyph-info-sign text-15 ml-5 cursor-pointer'
                        onClick={toogleRoleDialog}
                    />
                </>
            ),
            element: activeUser?.roles.length ? (
                activeUser.roles.map((role, index) => <p key={index}>{role.name}</p>)
            ) : (
                <NoData className='text-14' />
            ),
        },
    ];

    const systemItems: SectionItem[] = [
        {
            label: 'Status',
            element: <SystemUsersRowStatus status={activeUser?.status} />,
        },
        {
            label: 'Created',
            element: <DateTimeOrNoData value={activeUser?.createdAt} separator />,
        },
        {
            label: 'ID',
            element: activeUser?.id,
        },
    ];

    const closeSidebar = () => {
        dispatch(clearActiveElements());
    };

    return (
        <Sidebar
            fly
            className='SystemUsersSidebar__drawer'
            footerClassName='SystemUsersSidebar__footerWrapper'
            title={<p className='SystemUsersSidebar__header'>User information</p>}
            titleClassName='pl-10'
            closed={activeSidebar !== ActiveSidebar.USERS}
            onClose={closeSidebar}
            footer={
                <Footer
                    openUserDialog={() => openUserDialog(activeUser)}
                    openResetPasswordDialog={openResetPasswordDialog}
                    openDeleteUserDialog={openDeleteUserDialog}
                    user={activeUser}
                />
            }
        >
            <div className='SystemUsersSidebar__content'>
                <h4 className='SystemUsersSidebar__name'>{`${activeUser?.firstName} ${activeUser?.lastName}`}</h4>
                <SidebarSection title='Personal' items={personalItems} />
                <SidebarSection title='Work location' items={workLocationItems} />
                <SidebarSection title='Operational' items={operationalItems} />
                <SidebarSection title='System' items={systemItems} />
            </div>
        </Sidebar>
    );
};

export default SystemUsersSidebar;
